import { createElement } from 'react'
import { Layers } from 'lucide-react'
import { UniverseConditionsSection } from '../../components/nodes/universe-conditions-section'
import type {
  DateParameterDefinition,
  TextParameterDefinition,
  UniversalNodeSchema,
} from '../universal-node-schema'
import { UniversalNodeRegistry } from '../universal-node-schema'

/**
 * Stock Selection (type `universe`): the ticker days this strategy runs
 * against. Named "Universe" until Chris renamed it, 2026-10-08; the type,
 * the output id and EdgeScript's `universe` keyword keep the old name, since
 * saved strategies and scripts carry them.
 *
 * The second axis of a backtest. A run is logic times universe, and until now
 * the universe existed only as anonymous filter state inside the run modal, so
 * nothing on screen said which sample a number came from. Putting it on the
 * canvas makes it visible, makes it the strategy's pin, and lets the price data
 * node pick its preview ticker and day from the resolved set rather than from a
 * hand-typed field.
 *
 * Editor-only. It never reaches the run engine (see `editor-only-nodes.ts`):
 * the sample is the batch runner's concern, carried on the run itself, not
 * something the per-ticker engine evaluates. Its single-run ticker and day do
 * reach it, copied onto every Price Data when a run is sent
 * (`utils/run-ticker-day.ts`), because the engine still reads them there.
 *
 * Structured state is serialized into text parameters, following `csvData`,
 * which stores its column selection the same way. `filters` holds a
 * QueryFilterGroup as JSON; `inputCount` and `resolvedAt` are the cached result
 * of the last resolve, because resolving costs a query and a canvas re-renders
 * constantly.
 *
 * Design: docs/superpowers/specs/2026-08-31-backtest-strategy-workspace-design.md
 */

/**
 * The ticker and day the chart previews and a single run runs (Chris,
 * 2026-10-28), moved here from Price Data, which shows neither while a Stock
 * Selection is connected. The face's Preview block draws them, so the generic rows stay hidden. Empty by
 * default: an empty field reads the main Price Data's (`strategyTickerDay`),
 * which keeps a strategy saved before the move, or a template that dates its
 * Price Data, on its own day.
 */
const runSymbol: TextParameterDefinition = {
  name: 'symbol',
  displayName: 'Symbol',
  type: 'text',
  format: 'ticker',
  default: '',
  hidden: true,
}
const runTargetDate: DateParameterDefinition = {
  name: 'targetDate',
  displayName: 'Target date',
  type: 'date',
  default: '',
  hidden: true,
}

const universeSchema: UniversalNodeSchema = {
  type: 'universe',
  // The sample, not logic: never sent to the engine. The AI's node catalog
  // leaves it out (the builder owns entry and exit logic, not the sample);
  // EdgeScript keeps it, since a strategy script names its universe
  // (`universe "<name>"`, checks E042/E043).
  compound: { engine: 'editor-only', catalog: { ai: false } },
  displayName: 'Stock Selection',
  // The connection pill on Price Data reads this (it read "UNIVERSE", the
  // type in capitals, before the node had a face).
  face: { shortName: 'Stock Selection' },
  category: 'data',
  icon: Layers,
  description: 'The ticker days this strategy runs against',
  inputs: [],
  outputs: [
    {
      id: 'universe',
      displayName: 'Stock Selection',
      type: 'array',
    },
  ],
  parameters: [
    {
      // Blank means an ad-hoc filter set that has not been saved. A named
      // universe is a filter preset with source 'manual'; the per-run snapshot
      // the runner writes stays source 'batch_execution' and never appears in
      // the picker.
      // Edited from the node's Conditions block as a full-width field (Chris,
      // 2026-09-03), so the generic parameter row stays hidden.
      name: 'universeName',
      displayName: 'Name',
      type: 'text',
      default: '',
      hidden: true,
    },
    {
      name: 'filterPresetId',
      displayName: 'Saved universe',
      type: 'text',
      default: '',
      hidden: true,
    },
    {
      // QueryFilterGroup as JSON. The same shape the History page's filters
      // produce, so the config sheet can reuse ApplyGateSheet verbatim. Hidden:
      // a serialized tree is not something to edit in a node body.
      name: 'filters',
      displayName: 'Conditions',
      type: 'text',
      default: '',
      hidden: true,
    },
    {
      // Cached result of the last resolve. Hidden: it is read on the node's
      // Conditions section, never typed.
      name: 'inputCount',
      displayName: 'Ticker days',
      type: 'int',
      default: 0,
      hidden: true,
    },
    {
      // Set by the resolve, never by hand. Its presence is what distinguishes
      // "resolved to zero rows" from "never resolved".
      name: 'resolvedAt',
      displayName: 'Resolved at',
      type: 'text',
      default: '',
      hidden: true,
    },
    runSymbol,
    runTargetDate,
  ],
  ui: {
    // The Conditions block: enabled filters as chips, the last resolve, and
    // the button that opens the config sheet. Without it the filters were
    // reachable only from the hover tooltip's sliders icon. Then the Preview
    // rows, last before the outputs.
    customSections: [
      {
        type: 'custom',
        position: 'after-parameters',
        render: (data) =>
          createElement(UniverseConditionsSection, { data, preview: [runSymbol, runTargetDate] }),
      },
    ],
  },
}

export const universeSchemas: UniversalNodeSchema[] = [universeSchema]

export function registerUniverseSchemas() {
  universeSchemas.forEach((schema) => {
    UniversalNodeRegistry.register(schema)
  })
}
